import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { MapPin, Camera, Users, ArrowRight, Sparkles } from 'lucide-react';
import api from '../services/api';

// Fallback stock photos when not enough community photos
const FALLBACK_PHOTOS = [
  {
    url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&h=600&fit=crop',
    title: 'Mountain Adventures',
    caption: 'Every summit has a story',
    source: 'stock',
  },
  {
    url: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=1200&h=600&fit=crop',
    title: 'Urban Exploration',
    caption: 'Discover hidden gems in every city',
    source: 'stock',
  },
  {
    url: 'https://images.unsplash.com/photo-1473496169904-658ba7c44d8a?w=1200&h=600&fit=crop',
    title: 'Coastal Escapes',
    caption: 'Leave your mark by the ocean',
    source: 'stock',
  },
  {
    url: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&h=600&fit=crop',
    title: 'Road Trip Chronicles',
    caption: 'Document your journey, mile by mile',
    source: 'stock',
  },
];

const MIN_PHOTOS = 4;

export const HeroTravel = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [photos, setPhotos] = useState(FALLBACK_PHOTOS);

  // Fetch community photos on mount
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const data = await api.get('/featured-photos?limit=8');
        if (data?.success && data.photos?.length > 0) {
          const communityPhotos = data.photos;
          // If we have enough community photos, use them; otherwise mix with fallbacks
          if (communityPhotos.length >= MIN_PHOTOS) {
            setPhotos(communityPhotos);
          } else {
            // Fill remaining slots with fallbacks
            const needed = MIN_PHOTOS - communityPhotos.length;
            setPhotos([...communityPhotos, ...FALLBACK_PHOTOS.slice(0, needed)]);
          }
        }
      } catch (err) {
        // Silently fall back to stock photos
        console.log('Using stock photos for hero');
      }
    };
    fetchPhotos();
  }, []);

  // Rotate images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % photos.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [photos.length]);

  const currentPhoto = photos[currentImageIndex];

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-stone-900 via-emerald-950 to-stone-900">
      {/* Background Image Carousel with Warm Overlay */}
      <div className="absolute inset-0">
        {photos.map((photo, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={photo.url}
              alt={photo.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-stone-900/70 via-stone-900/30 to-stone-900/75"></div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-900/30 backdrop-blur-md rounded-full border border-amber-400/30 mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-100 font-medium" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
              Your Digital Travel Journal & Guestbook
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 4px 16px rgba(0,0,0,0.4)' }}>
            I Was Here.me
            <br />
            <span className="text-amber-100" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 4px 16px rgba(0,0,0,0.4)' }}>
              Every Person, Item and Location has a story to tell.
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl md:text-2xl text-amber-100/90 mb-4 max-w-3xl mx-auto leading-relaxed" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.7), 0 2px 10px rgba(0,0,0,0.3)' }}>
            Celebrate your travels and create lasting memories
          </p>
          <p className="text-base sm:text-lg text-stone-300 mb-12 max-w-2xl mx-auto" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
            Track your adventures, sign digital guestbooks at amazing locations, and share your journey with travelers worldwide. 
            No spray paint needed—just authentic stories and connections.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button
              size="lg"
              onClick={() => navigate('/create-location')}
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-8 py-6 text-lg font-semibold shadow-2xl shadow-emerald-900/50 hover:shadow-emerald-900/70 transition-all border border-emerald-500/30"
              data-testid="hero-create-guestbook-btn"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Create a Guestbook
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              onClick={() => navigate('/create-item')}
              variant="outline"
              className="bg-amber-900/20 backdrop-blur-md border-2 border-amber-400/40 text-amber-100 hover:bg-amber-900/40 px-8 py-6 text-lg font-semibold"
              data-testid="hero-track-item-btn"
            >
              <Camera className="w-5 h-5 mr-2" />
              Track an Item
            </Button>
          </div>

          {/* Feature Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="bg-emerald-900/30 backdrop-blur-md rounded-2xl p-6 border border-emerald-500/20 hover:bg-emerald-900/40 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>Sign Guestbooks</h3>
              <p className="text-stone-300 text-sm" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                Leave your digital signature at amazing locations around the world
              </p>
            </div>

            <div className="bg-amber-900/20 backdrop-blur-md rounded-2xl p-6 border border-amber-400/20 hover:bg-amber-900/30 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>Track Adventures</h3>
              <p className="text-stone-300 text-sm" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                Follow your traveling items as they journey across continents
              </p>
            </div>

            <div className="bg-stone-800/30 backdrop-blur-md rounded-2xl p-6 border border-stone-500/20 hover:bg-stone-800/40 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-stone-600 to-stone-700 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>Share Stories</h3>
              <p className="text-stone-300 text-sm" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                Connect with a global community of travelers and adventurers
              </p>
            </div>
          </div>

          {/* Photo Caption */}
          <div className="mt-12 text-center">
            <p className="text-stone-400 text-sm italic">
              {currentPhoto?.caption || ''}
            </p>
            {currentPhoto?.source === 'community' && currentPhoto?.location && (
              <p className="text-stone-500 text-xs mt-1 flex items-center justify-center gap-1">
                <MapPin className="w-3 h-3" />
                {currentPhoto.location}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Image Indicators */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex gap-2">
        {photos.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentImageIndex(idx)}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentImageIndex 
                ? 'bg-amber-400 w-6' 
                : 'bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-amber-400/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-amber-400/60 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};
